import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Heading,
  Text,
  Spinner,
  Button,
  Badge,
  Divider,
  Link,
} from "@chakra-ui/react";

interface RecordingData {
  call_id: string;
  recording_url: string;
  start_timestamp?: number;
  end_timestamp?: number;
  duration_ms?: number;
  from_number?: string;
  to_number?: string;
  call_status?: string;
  agent_id?: string;
}

function formatDuration(ms?: number): string {
  if (!ms || ms <= 0) return "—";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatDate(ts?: number): string {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return "—";
  }
}

export default function RecordingPlayer() {
  const { callId } = useParams<{ callId: string }>();
  const [data, setData] = useState<RecordingData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const serverUrl = process.env.REACT_APP_SERVER_URL || "";
    setLoading(true);
    setError(null);
    fetch(`${serverUrl}/api/recordings/${encodeURIComponent(callId || "")}`)
      .then(async (r) => {
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.error || "Recording not available");
        }
        return r.json();
      })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Recording not available");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [callId]);

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg="gray.50"
      px={4}
      py={10}
    >
      <Box
        w="100%"
        maxW="560px"
        bg="white"
        borderRadius="xl"
        boxShadow="lg"
        p={{ base: 6, md: 8 }}
      >
        <Heading size="md" mb={1}>
          Call Recording
        </Heading>
        <Text fontSize="sm" color="gray.500" mb={6}>
          {callId}
        </Text>

        {loading && (
          <Flex justify="center" py={10}>
            <Spinner size="lg" color="blue.500" />
          </Flex>
        )}

        {!loading && error && (
          <Box
            bg="red.50"
            border="1px solid"
            borderColor="red.200"
            borderRadius="md"
            p={4}
          >
            <Text color="red.600" fontWeight="medium">
              {error}
            </Text>
            <Text color="gray.500" fontSize="sm" mt={1}>
              This recording may not be available yet, or the link is invalid.
            </Text>
          </Box>
        )}

        {!loading && !error && data && (
          <VStack align="stretch" spacing={5}>
            <audio
              controls
              src={data.recording_url}
              style={{ width: "100%" }}
            >
              Your browser does not support the audio element.
            </audio>

            <Divider />

            <VStack align="stretch" spacing={2} fontSize="sm">
              {(data.from_number || data.to_number) && (
                <HStack justify="space-between">
                  <Text color="gray.500">From → To</Text>
                  <Text fontWeight="medium">
                    {(data.from_number || "—") + "  →  " + (data.to_number || "—")}
                  </Text>
                </HStack>
              )}
              <HStack justify="space-between">
                <Text color="gray.500">Date</Text>
                <Text fontWeight="medium">{formatDate(data.start_timestamp)}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text color="gray.500">Duration</Text>
                <Text fontWeight="medium">{formatDuration(data.duration_ms)}</Text>
              </HStack>
              {data.call_status && (
                <HStack justify="space-between">
                  <Text color="gray.500">Status</Text>
                  <Badge colorScheme="blue">{data.call_status}</Badge>
                </HStack>
              )}
            </VStack>

            <Link
              href={data.recording_url}
              download
              isExternal
              _hover={{ textDecoration: "none" }}
            >
              <Button colorScheme="blue" variant="outline" size="sm" w="100%">
                Download recording
              </Button>
            </Link>
          </VStack>
        )}
      </Box>
    </Flex>
  );
}
